import type { JobDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import type { PaymentInformationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/payment';
import type { OnlineActivityDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/activity';
import type { DSSPaymentFrequency } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { BankAccountFieldProps } from '../../../recoil/types/types';
import {
    Box,
    GridItem,
    RadioField,
    RadioInput,
    validateAccountNumber,
    validateBankNumber,
    validateCpr,
    validateCvr
} from '@flos/react-ui';
import { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { usePaymentInformationHook } from '../../../hooks/usePaymentInformation';
import { jobAtom } from '../../../recoil/atoms/jobAtom';
import { fetchedPaymentInformationAtom } from '../../../recoil/atoms/fetchedPaymentInformationAtom';
import { updatedPaymentInformationAtom } from '../../../recoil/atoms/updatedPaymentInformationAtom';
import { bankAccountValidityStatusAtom } from '../../../recoil/atoms/bankAccountValidityStatusAtom';
import { hasCoAccountHolderAtom } from '../../../recoil/atoms/hasCoAccountHolderAtom';
import { paymentFrequencyMap } from '../../../utils/currencyUtil';
import { useCreateActivityHook } from '../../../hooks/useCreateActivity';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import { convertDateToLocalDateDTO } from '../../../utils/dateUtil';
import { useHippoText } from '../../../hooks/useHippoText';
import { track } from '../../../utils/trackingUtil';
import AccordionWizardTemplate from '../components/AccordionWizardTemplate';
import StringParagraph from '../components/StringParagraph';
import PaymentInformationErrorModal from '../components/PaymentInformationErrorModal';
import BankAccountFields from './BankAccountFields';
import { allCoverablesAtom } from '../../../recoil/atoms/allCoverablesAtom';
import { jobInfoAtom } from '../../../recoil/atoms/jobInfoAtom';
import { PAYMENT_PLAN } from '../../../constants/paymentMethods';

const PaymentInformationStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const { updatePaymentInformation } = usePaymentInformationHook();
    const { createActivity } = useCreateActivityHook();
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const [paymentInformation, setPaymentInformation] = useRecoilState(
        fetchedPaymentInformationAtom
    );
    const coverables = useRecoilValue(allCoverablesAtom);
    const [paymentInfo, setPaymentInfo] = useState<BankAccountFieldProps>({
        accountNumber: paymentInformation.accountNumber ?? '',
        bankReference: paymentInformation.bankReference ?? ''
    });
    const accountHolder = useRecoilValue(accountHolderAtom);
    const updatedPaymentInfo = useRecoilValue(updatedPaymentInformationAtom);
    const isBankAccountValid = useRecoilValue(bankAccountValidityStatusAtom);
    const hasCoAccountHolder = useRecoilValue(hasCoAccountHolderAtom);
    const { isPolicyChange } = useRecoilValue(jobInfoAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [showInformationErrorModal, setShowInformationErrorModal] = useState(false);
    const [isFirstPaymentError, setIsFirstPaymentError] = useState(true);
    const { selectedPaymentFrequency = paymentFrequencyMap.monthly } = jobData;
    const {
        paymentMethod = PAYMENT_PLAN.MPS,
        yearlyAllowedPaymentMethods = [],
        monthlyAllowedPaymentMethods = []
    } = paymentInformation;
    const showFIKPayment =
        !!coverables?.changeOfOwnersApartments?.length ||
        !!coverables?.changeOfOwnersHouses?.length;
    const { cpr, cvr } = updatedPaymentInfo;
    const isSelectedPaymentFrequencyOneTime =
        selectedPaymentFrequency === paymentFrequencyMap.onetime;
    const isSelectedPaymentFrequencyMonthly =
        selectedPaymentFrequency === paymentFrequencyMap.monthly;
    const paymentPlan = isSelectedPaymentFrequencyMonthly
        ? monthlyAllowedPaymentMethods
        : yearlyAllowedPaymentMethods;
    const shouldRenderFIKOption =
        (!isSelectedPaymentFrequencyMonthly && paymentPlan.includes(PAYMENT_PLAN.FIK)) ||
        showFIKPayment;
    const shouldRenderBSOption = paymentPlan.includes(PAYMENT_PLAN.BS);
    const shouldRenderMPSOption = paymentPlan.includes(PAYMENT_PLAN.MPS);
    const getHippoText = useHippoText();

    const hippoText = {
        paymentFrequencyDescription: getHippoText(
            accountHolder.isCVR
                ? 'DSS-PAYMENT-INFO-SCREEN-CVR-FREQUENCY-HEADER'
                : 'DSS-PAYMENT-INFO-SCREEN-FREQUENCY-HEADER'
        ),
        monthlyOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-MONTHLY'),
        annualOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-YEARLY'),
        oneTimeOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-ONE-TIME'),
        methodDescription: getHippoText(
            accountHolder.isCVR
                ? 'DSS-PAYMENT-INFO-SCREEN-CVR-METHOD-HEADER'
                : 'DSS-PAYMENT-INFO-SCREEN-METHOD-HEADER'
        ),
        bsOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-BS-HEADER'),
        monthlyFee: getHippoText('DSS-PAYMENT-INFO-SCREEN-MONTHLY-FEE-TOOLTIP'),
        annualFee: getHippoText('DSS-PAYMENT-INFO-SCREEN-YEARLY-FEE-TOOLTIP'),
        fikOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-FIK-HEADER'),
        fikFee: getHippoText('DSS-PAYMENT-INFO-SCREEN-FEE-TOOLTIP'),
        mpsOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-MPS-HEADER'),
        mpsFee: getHippoText('DSS-PAYMENT-INFO-MPS-SCREEN-MONTHLY-FEE-TOOLTIP'),
        bsPolicyChange: getHippoText('DSS-PAYMENT-INFO-SCREEN-POLICYCHANGE-BS'),
        mpsPolicyChange: getHippoText('DSS-PAYMENT-INFO-SCREEN-POLICYCHANGE-MPS'),
        fikPolicyChange: getHippoText('DSS-PAYMENT-INFO-SCREEN-POLICYCHANGE-FIK')
    };

    const getCollectionFeeText = (): string => {
        return isSelectedPaymentFrequencyMonthly ? hippoText.monthlyFee : hippoText.annualFee;
    };

    const isFormValid =
        !!selectedPaymentFrequency &&
        !!paymentMethod &&
        paymentMethod === PAYMENT_PLAN.BS &&
        !isPolicyChange
            ? validateBankNumber(paymentInfo.bankReference) &&
              validateAccountNumber(paymentInfo.accountNumber) &&
              (hasCoAccountHolder
                  ? (!accountHolder.isCVR && validateCpr(cpr?.replace(/-/g, ''))) ||
                    (accountHolder.isCVR && validateCvr(cvr ?? ''))
                  : true) &&
              isBankAccountValid
            : true;

    const onNextClick = (): void => {
        if (isPolicyChange) {
            goNext();
        } else {
            setIsLoading(true);
            if (paymentInfo.accountNumber !== '' && paymentInfo.bankReference !== '') {
                setPaymentInformation((prev) => {
                    return {
                        ...prev,
                        accountNumber: paymentInfo.accountNumber,
                        bankReference: paymentInfo.bankReference
                    };
                });
            }
            void updatePaymentInformation(paymentInfo)
                .then(() => {
                    track('Payment information step', [
                        { paymentMethod },
                        { paymentFrequency: selectedPaymentFrequency }
                    ]);
                    goNext();
                })
                .catch(async () => {
                    if (!isFirstPaymentError) {
                        const onlineActivity: OnlineActivityDTO = {
                            activityCode: 'dss_unexpected_error_phone_req',
                            phoneNumber: accountHolder.phoneNumber,
                            targetDate: convertDateToLocalDateDTO(new Date())
                        };
                        await createActivity(onlineActivity);
                    }
                    setIsLoading(false);
                    setShowInformationErrorModal(true);
                });
        }
    };

    const closeModal = (): void => {
        setShowInformationErrorModal(false);
        setIsFirstPaymentError(false);
    };

    const changePaymentMethod = (value: string): void => {
        setPaymentInformation((prevData: PaymentInformationDTO) => ({
            ...prevData,
            paymentMethod: value
        }));
    };

    const onPaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        changePaymentMethod(event.target.value);
    };

    const onPaymentFrequencyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        if (!paymentPlan.includes(value)) {
            const paymentMethod = accountHolder.hasPhysicalEmail
                ? PAYMENT_PLAN.BS
                : PAYMENT_PLAN.MPS;
            changePaymentMethod(paymentMethod);
        }
        setJobData((prevData: JobDTO) => ({
            ...prevData,
            selectedPaymentFrequency: value as DSSPaymentFrequency
        }));
    };

    const policyChangePaymentContent = (): string => {
        switch (paymentMethod) {
            case PAYMENT_PLAN.BS:
                return hippoText.bsPolicyChange;
            case PAYMENT_PLAN.FIK:
                return hippoText.fikPolicyChange;
            case PAYMENT_PLAN.MPS:
                return hippoText.mpsPolicyChange;
            default:
                return '';
        }
    };

    const paymentSectionContent = (): React.JSX.Element => {
        return (
            <Box>
                <GridItem span={12}>
                    <RadioField
                        description={
                            <span className="h6">{hippoText.paymentFrequencyDescription}</span>
                        }
                        value={selectedPaymentFrequency}
                        onChange={onPaymentFrequencyChange}
                        required
                    >
                        {!isSelectedPaymentFrequencyOneTime && (
                            <RadioInput
                                value={paymentFrequencyMap.monthly}
                                label={hippoText.monthlyOption}
                            />
                        )}
                        {!isSelectedPaymentFrequencyOneTime && (
                            <RadioInput
                                value={paymentFrequencyMap.annual}
                                label={hippoText.annualOption}
                            />
                        )}
                        {isSelectedPaymentFrequencyOneTime && (
                            <RadioInput
                                value={paymentFrequencyMap.onetime}
                                label={hippoText.oneTimeOption}
                            />
                        )}
                    </RadioField>
                </GridItem>
                <GridItem className="u-spacing-stack-top-4" span={12}>
                    <RadioField
                        description={<span className="h6">{hippoText.methodDescription}</span>}
                        value={paymentMethod}
                        onChange={onPaymentMethodChange}
                        required
                    >
                        {shouldRenderMPSOption && (
                            <RadioInput value={PAYMENT_PLAN.MPS} label={hippoText.mpsOption} />
                        )}
                        {shouldRenderMPSOption && (
                            <StringParagraph small>{hippoText.mpsFee}</StringParagraph>
                        )}
                        {shouldRenderBSOption && (
                            <RadioInput value={PAYMENT_PLAN.BS} label={hippoText.bsOption} />
                        )}
                        {shouldRenderBSOption && (
                            <StringParagraph small>{getCollectionFeeText()}</StringParagraph>
                        )}
                        {shouldRenderFIKOption && (
                            <RadioInput value={PAYMENT_PLAN.FIK} label={hippoText.fikOption} />
                        )}
                        {shouldRenderFIKOption && (
                            <StringParagraph small>{hippoText.fikFee}</StringParagraph>
                        )}
                    </RadioField>
                </GridItem>
                {paymentMethod === PAYMENT_PLAN.BS && shouldRenderBSOption && (
                    <BankAccountFields paymentInfo={paymentInfo} setPaymentInfo={setPaymentInfo} />
                )}
                {showInformationErrorModal && (
                    <PaymentInformationErrorModal
                        showModal={showInformationErrorModal}
                        closeModal={closeModal}
                        isFirstPaymentError={isFirstPaymentError}
                    />
                )}
            </Box>
        );
    };

    return (
        <AccordionWizardTemplate
            goNext={onNextClick}
            disableNext={!isFormValid}
            isNextBtnLoading={isLoading}
        >
            {isPolicyChange ? policyChangePaymentContent() : paymentSectionContent()}
        </AccordionWizardTemplate>
    );
};

export default PaymentInformationStep;
